import React, { FC, useState, useCallback, ChangeEvent, useMemo } from 'react'
import makeStyles from '@mui/styles/makeStyles';
import DataGrid2, { IDataGrid2_Column } from '../datagrid2/DataGrid'

import TextField from '@mui/material/TextField'
import TinyButon from './TinyButton'

import {
  ILayout_Table,
} from '@kaidam/fc-shared/src/types/layout'
import Window from '../dialog/Window'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  toolbar: {
    padding: theme.spacing(1),
    flexGrow: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  button: {
    marginRight: theme.spacing(1),
  },
  content: {
    flexGrow: 1,
    overflow: 'auto',
  },
}))

interface TableEditorProps {
  data: ILayout_Table,
  onChange: {
    (data: ILayout_Table): void,
  },
}

const TableEditor: FC<React.PropsWithChildren<TableEditorProps>> = ({
  data,
  onChange,
}) => {

  const classes = useStyles()
  const [ selectedPos, setSelectedPos ] = useState<{
    idx: number,
    rowIdx: number, 
  }>(null)
  const [ editingColumnIndex, setEditingColumnIndex ] = useState(-1)
  const [ columnName, setColumnName ] = useState('')

  const useData = useMemo(() => {
    if(data) return data
    return {
      data: [],
      config: {
        columns: [],
      },
    }
  }, [
    data,
  ])

  const onAddColumn = useCallback(() => {
    const columnCount = useData.config.columns.length
    setColumnName('')
    setEditingColumnIndex(columnCount)
  }, [
    useData,
  ])

  const onAddRow = useCallback(() => {
    const newData = Object.assign({}, useData, {
      data: useData.data.concat([[]]),
    })
    onChange(newData)
  }, [
    useData,
  ])

  const onDeleteColumn = useCallback(() => {
    const newColumns = useData.config.columns.filter((c, i) => i != selectedPos.idx)
    const newData = Object.assign({}, useData, {
      config: Object.assign({}, useData.config, {
        columns: newColumns,
      }),
    })
    onChange(newData)
  }, [
    selectedPos,
    useData,
  ])

  const onDeleteRow = useCallback(() => {
    const newData = Object.assign({}, useData, {
      data: useData.data.filter((r, i) => i != selectedPos.rowIdx),
    })
    onChange(newData)
  }, [
    selectedPos,
    useData,
  ])

  const onSubmitColumn = useCallback(() => {
    if(!columnName) {
      setEditingColumnIndex(-1)
      setColumnName('')
      return
    }
    const newColumns = useData.config.columns.concat([columnName])
    const newData = Object.assign({}, useData, {
      config: Object.assign({}, useData.config, {
        columns: newColumns,
      }),
    })
    onChange(newData)
    setEditingColumnIndex(-1)
    setColumnName('')
  }, [
    columnName,
    onChange,
    editingColumnIndex,
    useData,
  ])

  const onChangeRows = useCallback((rows: any) => {
    const processedRows = rows.map(row => {
      return useData.config.columns.map((columnName, i) => row[`key_${i}`] || '')
    })
    const newData = Object.assign({}, useData, {
      data: processedRows,
    })
    onChange(newData)
  }, [
    onChange,
    useData,
  ])

  const columns = useMemo<IDataGrid2_Column<any>[]>(() => {
    return useData.config.columns.map((columnName, i) => {
      return {
        name: `key_${i}`,
        header: columnName,
        defaultFlex: 1,
        sortable: false,
      }
    })
  }, [
    useData,
  ])

  const rows = useMemo(() => {
    return useData.data.map((row, id) => {
      const data = useData.config.columns.reduce((all, columnName, i) => {
        all[`key_${i}`] = row[i] || ''
        return all
      }, {})
      return Object.assign({}, data, {
        id,
      })
    })
  }, [
    useData,
  ])

  return (
    <div className={ classes.root }>
      <div className={ classes.toolbar }>
        <TinyButon
          variant="outlined"
          className={ classes.button }
          onClick={ onAddColumn }
        >
          Add Column
        </TinyButon>
        <TinyButon
          variant="outlined"
          className={ classes.button }
          onClick={ onAddRow }
        >
          Add Row
        </TinyButon>
        {
          selectedPos && (
            <>
              <TinyButon
                variant="outlined"
                className={ classes.button }
                onClick={ onDeleteColumn }
              >
                Delete Column
              </TinyButon>
              <TinyButon
                variant="outlined"
                className={ classes.button }
                onClick={ onDeleteRow }
              >
                Delete Row
              </TinyButon>
            </>
          )
        }
      </div>
      <div className={ classes.content }>
        <DataGrid2
          editable
          columns={ columns }
          rows={ rows }
          onRowsChange={ onChangeRows }
          onSelect={ (rowIndex, cellIndex) => {
            setSelectedPos({
              idx: cellIndex,
              rowIdx: rowIndex,
            })
          }}
        />
      </div>
      {
        editingColumnIndex >= 0 && (
          <Window
            open
            size="sm"
            onSubmit={ onSubmitColumn }
            withCancel
            onCancel={ () => {
              setEditingColumnIndex(-1)
            }}
          >
            <TextField
              fullWidth
              label="Column Name"
              helperText="enter the name for the table column"
              value={ columnName }
              onChange={ (e: ChangeEvent<HTMLInputElement>) => {
                setColumnName(e.target.value)
              }}
            />
          </Window>
        )
      }
    </div>
  )
}

export default TableEditor